import {
	DangerousOutlined,
	InfoOutlined,
	TaskAltOutlined,
} from '@mui/icons-material';
import { Alert, AlertTitle } from '@mui/material';
import useVaxFormData from 'components/impfschaeden/assistent/hooks/useVaxFormData.js';
import {
	AutoResizeTextArea,
	Col,
	DropdownSelect,
	Form,
	FormGroup,
	FormHelperText,
	RadioButton,
	Row,
	TagInputNoNew,
} from 'components/ui';
import remove from 'lodash/remove';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { LexiconButton } from 'VaxForm_08_Impfschaeden/components/LexikonButton.js';
import { Toolbox } from 'VaxForm_08_Impfschaeden/components/Toolbox.js';

const StyledFormHelperText = styled(FormHelperText)`
	color: rgba(237, 108, 2, 0.7) !important;
`;

const InfoBox = styled(Alert)`
	& > div > div {
		margin-bottom: 5px;
	}
	& > div > div:first-child {
		margin-bottom: 8px;
	}
`;

const ImpfschaedenForm = ({ update, formData, validation }) => {
	const [t] = useTranslation();
	const { globHealthDamages, globDamageOccurred, globHealthResults } =
		useSelector((state) => state.masterData);
	const [search, setSearch] = useState('');
	const [showInfo, setShowInfo] = useState(false);
	const [open, setOpen] = useState(false);
	const { isFromInternet } = useVaxFormData();
	const health_damage_options = globHealthDamages
		?.map((item) => {
			let newItem = { ...item };
			if (item.lang_iso_code == 'translation') {
				newItem.name = t(
					'master_data.health_damage.' + item.health_damage_i18n_key
				);
			} else {
				newItem.name = newItem.health_damage_i18n_key;
			}
			return newItem;
		})
		.filter((item) => item.is_selectable == true && item.is_vax_damage == true)
		.sort((a, b) => a.name.localeCompare(b.name));

	const getDamageOccurredOptions = (damagesOccurred) => {
		const [t] = useTranslation(['translation']);
		let damageOccurredOptions = [];
		if (damagesOccurred && damagesOccurred.length) {
			damageOccurredOptions = damagesOccurred.map((damageOccurred) => ({
				value: damageOccurred.glob_damage_occurred_id,
				text: t(
					'master_data.damage_occurred.' + damageOccurred.occurred_i18n_key
				),
			}));
		}
		damageOccurredOptions.unshift({
			value: '',
			text: t('common.bitte_waehlen') + ' *',
		});
		return damageOccurredOptions;
	};

	const getHealthResultOptions = (healthResults) => {
		const [t] = useTranslation(['translation']);
		let healthResultOptions = [];
		if (healthResults && healthResults.length) {
			healthResultOptions = healthResults.map((healthResult) => ({
				value: healthResult.glob_health_result_id,
				text: t(
					'master_data.health_result.' + healthResult.health_result_i18n_key
				),
			}));
		}
		healthResultOptions.unshift({
			value: '',
			text: t('common.bitte_waehlen') + ' *',
		});
		return healthResultOptions;
	};

	const tag_options = (() => {
		if (search.length == 0) return health_damage_options;
		else
			return health_damage_options.concat({
				name:
					t('impfschaeden.erfassen.hinzufuegen_text', 'Hinzufügen: ') + search,
			});
	})();

	const hasNewTags = (() => {
		if (!formData?.vax_health_damage_tags) {
			return false;
		}
		const result = formData?.vax_health_damage_tags.filter((value) => {
			if (value?.lang_iso_code == 'translation') {
				return false;
			} else {
				return true;
			}
		});
		if (result.length) {
			return true;
		} else {
			return false;
		}
	})();

	return (
		<>
			<Row style={{ marginTop: '20px' }}>
				<Col xs={12} md={6}>
					<FormGroup className='mb-3' controlId='exampleForm.ControlTextarea1'>
						<Form.Label>
							{t('impfschaeden.erfassen.beschreibung_impfreaktion')}*
						</Form.Label>
						{isFromInternet && (
							<div
								className={
									'd-flex' +
									(showInfo
										? ' flex-column align-items-start'
										: ' flex-row align-items-center')
								}
							>
								{isFromInternet && showInfo && (
									<InfoBox
										icon={false}
										// iconMapping={{
										// 	info: (
										// <InfoOutlined
										// 	color='info'
										// 	onClick={() => setShowInfo(false)}
										// 	className='cursor-pointer'
										// />
										// 	),
										// }}
										severity='info'
										variant='outlined'
										className='mb-1'
										onClose={() => setShowInfo(false)}
									>
										<AlertTitle>
											<InfoOutlined
												color='info'
												onClick={() => setShowInfo(false)}
												className='cursor-pointer me-5'
											/>
											{t('impfschaeden.erfassen.wie_zitiere_ich_richtig')}
										</AlertTitle>
										<div>
											<TaskAltOutlined color='success' />
											<span className='ms-2'>
												Kürzen mit eckigen Klammern [...]
											</span>
										</div>
										<div>
											<TaskAltOutlined color='success' />
											<span className='ms-2'>
												Hinzufügen mit eckigen Klammern [Beispiel]
											</span>
										</div>
										<div>
											<TaskAltOutlined color='success' />
											<span className='ms-2'>
												Anmerkungen mit eckigen Klammern [Fall 1 von 4]
											</span>
										</div>
										<div>
											<TaskAltOutlined color='success' />
											<span className='ms-2'>
												Rechtschreibfehler korrigieren
											</span>
										</div>
										<div>
											<TaskAltOutlined color='success' />
											<span className='ms-2'>Emojis übernehmen 🙏 💉</span>
										</div>
										<div>
											<DangerousOutlined color='error' />
											<span className='ms-2'>den Bericht umschreiben</span>
										</div>
										<div>
											<DangerousOutlined color='error' />
											<span className='ms-2'>
												Angaben zum Impfhersteller verändern
											</span>
										</div>
										Danke! 💙
									</InfoBox>
								)}
								{isFromInternet && !showInfo && (
									<InfoOutlined
										color='info'
										onClick={() => setShowInfo(true)}
										className='cursor-pointer mb-1'
									/>
								)}
								<Toolbox open={open} setOpen={setOpen} />
							</div>
						)}
						<div className='position-relative mt-1'>
							{open && (
								<Button
									style={{ top: 0, left: '-50px', color: 'white' }}
									className='position-absolute'
									onClick={() => {
										update({
											description: '[...] ' + (formData?.description || ''),
										});
									}}
								>
									[...]
								</Button>
							)}
							<AutoResizeTextArea
								className='w-100'
								minRows={5}
								label={t('impfschaeden.erfassen.impfschaeden_beschreiben')}
								value={formData?.description || ''}
								onChange={(e) => {
									update({ description: e.target.value });
								}}
								isInvalid={validation['description']}
							/>
							{open && (
								<div
									style={{ color: 'white' }}
									className='d-flex justify-content-end'
								>
									<Button
										style={{ color: 'white' }}
										onClick={() => {
											update({
												description: (formData?.description || '') + ' [...]',
											});
										}}
									>
										[...]
									</Button>
									<Button
										style={{ color: 'white' }}
										className='ms-1'
										onClick={() => {
											update({
												description:
													(formData?.description || '') + ' [Fall 1 von x]',
											});
										}}
									>
										[Fall 1 von x]
									</Button>
								</div>
							)}
						</div>
					</FormGroup>
				</Col>
				<Col xs={12} md={6} className='mb-2'>
					<FormGroup>
						<Form.Label>
							{t('impfschaeden.erfassen.zeitraum_impfungen_schaeden')} *
						</Form.Label>
						<DropdownSelect
							aria-label={t(
								'impfschaeden.erfassen.zeitraum_impfungen_schaeden'
							)}
							onChange={(e) => {
								update({ vax_damage_occurred: e.target.value });
							}}
							value={formData?.vax_damage_occurred}
							options={getDamageOccurredOptions(globDamageOccurred)}
							isInvalid={validation['vax_damage_occurred']}
						/>
					</FormGroup>
					<FormGroup className='mb-3'>
						<Form.Label style={{ whiteSpace: 'nowrap' }}>
							{t('impfschaeden.erfassen.krankenhaus_einweisung')} *
						</Form.Label>
						<div style={{ display: 'flex', justifyContent: 'space-around' }}>
							<RadioButton
								name='hospitalized'
								value='ja'
								label={t('common.ja')}
								checked={formData?.hospitalized === 'ja'}
								onChange={(e) => update({ hospitalized: e.target.value })}
								isInvalid={validation['hospitalized']}
							/>
							<RadioButton
								name='hospitalized'
								value='nein'
								label={t('common.nein')}
								checked={formData?.hospitalized === 'nein'}
								onChange={(e) => update({ hospitalized: e.target.value })}
								isInvalid={validation['hospitalized']}
							/>
							<RadioButton
								name='hospitalized'
								value='unknown'
								label={t('common.unbekannt')}
								checked={formData?.hospitalized === 'unknown'}
								onChange={(e) => update({ hospitalized: e.target.value })}
								isInvalid={validation['hospitalized']}
							/>
						</div>
						{validation['hospitalized'] && (
							<FormHelperText style={{ color: '#dc3545' }}>
								{t('common.pflichtfeld')}
							</FormHelperText>
						)}
					</FormGroup>
					<FormGroup>
						<Form.Label style={{ whiteSpace: 'nowrap' }}>
							{t('impfschaeden.erfassen.ausgang_der_impfung')} *
						</Form.Label>
						<DropdownSelect
							aria-label={t('impfschaeden.erfassen.ausgang_der_impfung')}
							onChange={(e) => {
								update({
									glob_health_result_id: e.target.value,
								});
							}}
							value={formData?.glob_health_result_id}
							options={getHealthResultOptions(globHealthResults)}
							isInvalid={validation['glob_health_result_id']}
						/>
					</FormGroup>
				</Col>
			</Row>
			<Row>
				<Col className='d-flex flex-row'>
					<div className='flex-grow-1'>
						<TagInputNoNew
							options={tag_options}
							value={formData?.vax_health_damage_tags || []}
							onChange={(e, newValues) => {
								newValues = newValues.map((item) => {
									let newItem;
									if (typeof item == 'string') {
										newItem = { name: item };
									} else {
										newItem = { ...item };
									}
									newItem.name = newItem?.name?.replace(
										t('impfschaeden.erfassen.hinzufuegen_text', 'Hinzufügen: '),
										''
									);
									return newItem;
								});
								update({ vax_health_damage_tags: uniqBy(newValues, 'name') });
							}}
							onInputChange={(event, value) => {
								setSearch(value);
							}}
							label={
								t(
									'impfschaeden.erfassen.schlagworte_impfreaktion_beschreiben'
								) + '*'
							}
							isInvalid={validation['vax_health_damage_tags']}
						/>
						<FormHelperText>
							{t('impfschaeden.erfassen.hinweis_schlagworte')}
						</FormHelperText>
						{hasNewTags && (
							<StyledFormHelperText>
								{t('impfschaeden.erfassen.neue_tags_gelb')}
							</StyledFormHelperText>
						)}
					</div>
					{/* <div className='ms-3'>
						<a
							href={API_URL + 'v1/vax/get_lexicon'}
							// target='_blank'
							rel='noreferrer'
						>
							<Button className='p-2'>
								<MenuBook />
							</Button>
						</a>
					</div> */}
					{/* <div className='ms-3'>
						<a
							href={'/impfschaden/schlagworte/lexicon'}
							// target='_blank'
							rel='noreferrer'
						>
							<Button className='p-2' style={{ color: 'white' }}>
								<MenuBook />
							</Button>
						</a>
					</div>
					 */}
					<div className='ms-1'>
						<LexiconButton
							label={
								t(
									'impfschaeden.erfassen.schlagworte_impfreaktion_beschreiben'
								) + '*'
							}
							tag_options={tag_options}
							validation={validation}
							setSearch={setSearch}
							update={update}
							countProperty={'vax_health_damage_tags'}
							formData={formData}
							onSelect={(newTag) => {
								let newValues = formData
									? [...formData.vax_health_damage_tags]
									: [];
								if (
									newValues.find(
										(item) =>
											item.glob_health_damage_id == newTag.glob_health_damage_id
									)
								) {
									remove(newValues, (item) => {
										return (
											item.glob_health_damage_id == newTag.glob_health_damage_id
										);
									});
								} else {
									newValues = [...newValues, newTag];
								}

								update({
									vax_health_damage_tags: uniqBy(newValues, 'name'),
								});
							}}
							selected={formData?.vax_health_damage_tags?.map((item) => {
								return item.glob_health_damage_id;
							})}
						/>
					</div>
				</Col>
			</Row>
		</>
	);
};

ImpfschaedenForm.propTypes = {
	update: PropTypes.func,
	formData: PropTypes.object,
	validation: PropTypes.object,
};

export { ImpfschaedenForm };
